import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PayoutTrackingTable = ({ selectedRail, dateRange }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  const [expandedRows, setExpandedRows] = useState([]);

  const payoutData = [
    {
      id: 'PO-2026-001',
      date: '2026-01-18',
      rail: 'Bitcoin',
      railIcon: 'Bitcoin',
      railColor: '#F7931A',
      amount: 18450.32,
      transactions: 1247,
      status: 'processing',
      estimatedCompletion: '2 hours',
      batchId: 'BTC-20260118-001',
      details: {
        merchantFees: 12340.50,
        swapCommissions: 4280.82,
        otherRevenue: 1829.00,
        networkFees: 125.50
      }
    },
    {
      id: 'PO-2026-002',
      date: '2026-01-17',
      rail: 'Ethereum',
      railIcon: 'Hexagon',
      railColor: '#627EEA',
      amount: 12680.50,
      transactions: 892,
      status: 'completed',
      estimatedCompletion: 'Completed',
      batchId: 'ETH-20260117-001',
      details: {
        merchantFees: 8920.25,
        swapCommissions: 2840.25,
        otherRevenue: 920.00,
        networkFees: 85.30
      }
    },
    {
      id: 'PO-2026-003',
      date: '2026-01-17',
      rail: 'Stablecoin',
      railIcon: 'DollarSign',
      railColor: '#10B981',
      amount: 8520.00,
      transactions: 2341,
      status: 'pending',
      estimatedCompletion: '1 day',
      batchId: 'USDC-20260117-001',
      details: {
        merchantFees: 6240.00,
        swapCommissions: 1850.00,
        otherRevenue: 430.00,
        networkFees: 45.00
      }
    },
    {
      id: 'PO-2026-004',
      date: '2026-01-16',
      rail: 'Merchant',
      railIcon: 'Store',
      railColor: '#8B5CF6',
      amount: 5629.68,
      transactions: 456,
      status: 'completed',
      estimatedCompletion: 'Completed',
      batchId: 'MER-20260116-001',
      details: {
        merchantFees: 5629.68,
        swapCommissions: 0,
        otherRevenue: 0,
        networkFees: 0
      }
    },
    {
      id: 'PO-2026-005',
      date: '2026-01-15',
      rail: 'Bitcoin',
      railIcon: 'Bitcoin',
      railColor: '#F7931A',
      amount: 22340.15,
      transactions: 1589,
      status: 'completed',
      estimatedCompletion: 'Completed',
      batchId: 'BTC-20260115-001',
      details: {
        merchantFees: 15680.25,
        swapCommissions: 5240.90,
        otherRevenue: 1419.00,
        networkFees: 142.50
      }
    }
  ];

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev?.key === key && prev?.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const toggleRow = (id) => {
    setExpandedRows(prev => 
      prev?.includes(id) ? prev?.filter(rowId => rowId !== id) : [...prev, id]
    );
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'completed':
        return { color: 'text-success', bg: 'bg-success/20', icon: 'CheckCircle2', label: 'Completed' };
      case 'processing':
        return { color: 'text-warning', bg: 'bg-warning/20', icon: 'Clock', label: 'Processing' };
      case 'pending':
        return { color: 'text-muted-foreground', bg: 'bg-muted', icon: 'Circle', label: 'Pending' };
      case 'failed':
        return { color: 'text-error', bg: 'bg-error/20', icon: 'XCircle', label: 'Failed' };
      default:
        return { color: 'text-muted-foreground', bg: 'bg-muted', icon: 'Circle', label: 'Unknown' };
    }
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig?.key !== columnKey) {
      return <Icon name="ChevronsUpDown" size={14} className="text-muted-foreground" />;
    }
    return (
      <Icon 
        name={sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
        size={14} 
        className="text-primary" 
      />
    );
  };

  const sortedData = [...payoutData]?.sort((a, b) => {
    if (sortConfig?.direction === 'asc') {
      return a?.[sortConfig?.key] > b?.[sortConfig?.key] ? 1 : -1;
    }
    return a?.[sortConfig?.key] < b?.[sortConfig?.key] ? 1 : -1;
  });

  return (
    <div className="card mb-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
        <div>
          <h3 className="text-lg md:text-xl font-semibold text-foreground mb-1">
            Settlement Tracking
          </h3>
          <p className="text-sm text-muted-foreground">Monitor payout batches and settlement status</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" iconName="Filter">
            Filter
          </Button>
          <Button variant="outline" size="sm" iconName="Download">
            Export
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-3 px-4 text-sm text-muted-foreground font-semibold">
                <button 
                  onClick={() => handleSort('id')}
                  className="flex items-center gap-2 hover:text-foreground transition-smooth"
                >
                  Payout ID
                  <SortIcon columnKey="id" />
                </button>
              </th>
              <th className="text-left py-3 px-4 text-sm text-muted-foreground font-semibold">
                <button 
                  onClick={() => handleSort('date')}
                  className="flex items-center gap-2 hover:text-foreground transition-smooth"
                >
                  Date
                  <SortIcon columnKey="date" />
                </button>
              </th>
              <th className="text-left py-3 px-4 text-sm text-muted-foreground font-semibold">
                Payment Rail
              </th>
              <th className="text-right py-3 px-4 text-sm text-muted-foreground font-semibold">
                <button 
                  onClick={() => handleSort('amount')}
                  className="flex items-center justify-end gap-2 hover:text-foreground transition-smooth ml-auto"
                >
                  Amount
                  <SortIcon columnKey="amount" />
                </button>
              </th>
              <th className="text-right py-3 px-4 text-sm text-muted-foreground font-semibold">
                Transactions
              </th>
              <th className="text-left py-3 px-4 text-sm text-muted-foreground font-semibold">
                Status
              </th>
              <th className="text-left py-3 px-4 text-sm text-muted-foreground font-semibold">
                Completion
              </th>
              <th className="w-12"></th>
            </tr>
          </thead>
          <tbody>
            {sortedData?.map((payout) => {
              const statusConfig = getStatusConfig(payout?.status);
              return (
                <React.Fragment key={payout?.id}>
                  <tr className="border-b border-border hover:bg-muted/30 transition-smooth">
                    <td className="py-4 px-4">
                      <p className="text-sm font-semibold text-foreground">{payout?.id}</p>
                      <p className="text-xs text-muted-foreground">{payout?.batchId}</p>
                    </td>
                    <td className="py-4 px-4">
                      <p className="text-sm text-foreground">
                        {new Date(payout?.date)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </p>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-8 h-8 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: `${payout?.railColor}20` }}
                        >
                          <Icon name={payout?.railIcon} size={16} color={payout?.railColor} />
                        </div>
                        <span className="text-sm font-medium text-foreground">{payout?.rail}</span>
                      </div>
                    </td>
                    <td className="py-4 px-4 text-right">
                      <p className="text-sm font-bold text-foreground font-data">
                        ${payout?.amount?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </p>
                    </td>
                    <td className="py-4 px-4 text-right">
                      <p className="text-sm text-muted-foreground font-data">
                        {payout?.transactions?.toLocaleString()}
                      </p>
                    </td>
                    <td className="py-4 px-4">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${statusConfig?.bg} ${statusConfig?.color}`}>
                        <Icon name={statusConfig?.icon} size={12} />
                        {statusConfig?.label}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <p className="text-sm text-muted-foreground">{payout?.estimatedCompletion}</p>
                    </td>
                    <td className="py-4 px-4">
                      <button
                        onClick={() => toggleRow(payout?.id)}
                        className="p-1 rounded hover:bg-muted/50 transition-smooth"
                      >
                        <Icon 
                          name={expandedRows?.includes(payout?.id) ? 'ChevronUp' : 'ChevronDown'} 
                          size={16} 
                          className="text-muted-foreground"
                        />
                      </button>
                    </td>
                  </tr>
                  {expandedRows?.includes(payout?.id) && (
                    <tr className="bg-muted/20">
                      <td colSpan="8" className="py-4 px-4">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 pl-4">
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">Merchant Fees</p>
                            <p className="text-sm font-semibold text-foreground font-data">
                              ${payout?.details?.merchantFees?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">Swap Commissions</p>
                            <p className="text-sm font-semibold text-foreground font-data">
                              ${payout?.details?.swapCommissions?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">Other Revenue</p>
                            <p className="text-sm font-semibold text-foreground font-data">
                              ${payout?.details?.otherRevenue?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">Network Fees</p>
                            <p className="text-sm font-semibold text-error font-data">
                              -${payout?.details?.networkFees?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                            </p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PayoutTrackingTable;